import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import TVCard from '@/components/base/TVCard';
import { tvSeriesData } from '@/mocks/tvSeries';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const series = tvSeriesData.find((s) => s.id === id);

  if (!series) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，你查找的剧集不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedSeries = tvSeriesData
    .filter((s) => s.categorySlug === series.categorySlug && s.id !== series.id)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <article className="max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: series.category, href: `/category/${series.categorySlug}` },
                { label: series.title },
              ]}
            />
          </div>

          {/* Article Header - Schema.org Article */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-1">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={series.image}
                  alt={series.title}
                  title={`${series.title} - 窝窝影院免费在线观看`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {series.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                  {series.rating} 分
                </span>
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                  {series.year}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                  共{series.totalEpisodes}集
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap">
                  {series.region}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  series.status === '已完结'
                    ? 'bg-foreground-100 text-foreground-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {series.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                {series.genre.map((g) => (
                  <span key={g} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    {g}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {series.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-foreground-500 min-w-[3rem]">导演:</span>
                  <span className="text-foreground-800 font-medium">{series.director}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-500 min-w-[3rem]">主演:</span>
                  <span className="text-foreground-800 font-medium">{series.actors.join('、')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-500 min-w-[3rem]">更新:</span>
                  <span className="text-foreground-800 font-medium">{series.updateDay}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-500 min-w-[3rem]">播放:</span>
                  <span className="text-foreground-800 font-medium">{(series.views / 10000).toFixed(0)}万次</span>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-play-fill w-4 h-4 flex items-center justify-center"></i>
                  立即播放
                </button>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-100 hover:text-accent-500 transition-colors cursor-pointer">
                  <i className="ri-heart-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-100 hover:text-accent-500 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Episode List */}
          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
              剧集列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共{series.totalEpisodes}集</span>
            </h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
              {series.episodes.slice(0, 36).map((ep) => (
                <button
                  key={ep.id}
                  className="px-3 py-2 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-500 text-sm text-foreground-700 transition-all duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  {ep.episodeNumber}
                </button>
              ))}
            </div>
            {series.episodes.length > 36 && (
              <button className="mt-3 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap">
                展开全部 {series.totalEpisodes} 集
              </button>
            )}
          </section>

          {/* Content / Introduction */}
          <section className="mb-12 max-w-3xl">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">剧情介绍</h2>
            <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
              {series.content.split('\n\n').map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </section>

          {/* Related Series */}
          {relatedSeries.length > 0 && (
            <section className="mb-12">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {relatedSeries.map((s) => (
                  <TVCard key={s.id} series={s} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${series.title} - 窝窝影院免费在线观看`,
            description: series.description,
            image: series.image,
            author: {
              '@type': 'Organization',
              name: '窝窝影院',
            },
            publisher: {
              '@type': 'Organization',
              name: '窝窝影院',
            },
            datePublished: `${series.year}-01-01`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://wowocinema.com/article/${series.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://wowocinema.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: series.category,
                item: `https://wowocinema.com/category/${series.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: series.title,
              },
            ],
          }),
        }}
      />
    </div>
  );
}